"use client";

import NavigationArrow from "@/assets/NavigationArrow";
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface SpecialisationMetadata {
  id: string;
  card_title: string;
  card_description: string;
  detail_title?: string;
  detail_subtitle?: string;
  detail_description?: string;
  icon?: string;
  icon_alt?: string;
  hover_icon?: string;
  hover_icon_alt?: string;
  priority?: number;
  meta_status?: boolean;
  // Mobile Variant 1 (carousel) styling
  mobile_v1_icon?: string;
  mobile_v1_icon_alt?: string;
  mobile_v1_title_card_color?: string;
  mobile_v1_title_text_color?: string;
  mobile_v1_description_card_color?: string;
  mobile_v1_description_text_color?: string;
}

interface CourseSpecialisationsCarouselProps {
  title: string;
  titleTextColor: string;
  backgroundColor: string;
  displayData: SpecialisationMetadata[];
  // Global navigation icons (from parent)
  leftNavIcon?: string;
  leftNavIconAlt?: string;
  rightNavIcon?: string;
  rightNavIconAlt?: string;
  showNavigationButtons?: boolean;
  onNavigationClick?: (
    direction: "prev" | "next",
    currentIndex: number
  ) => void;
}

export const CourseSpecialisationsCarousel = React.forwardRef<
  HTMLElement,
  CourseSpecialisationsCarouselProps
>(
  (
    {
      title,
      titleTextColor,
      backgroundColor = "#FFFFFF",
      displayData,
      leftNavIcon,
      leftNavIconAlt = "Previous",
      rightNavIcon,
      rightNavIconAlt = "Next",
      onNavigationClick,
      showNavigationButtons = true,
    },
    ref
  ) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
      loop: false,
      align: "center",
      containScroll: "trimSnaps",
    });

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(true);
    const lastNavigationDirectionRef = useRef<"prev" | "next" | null>(null);

    const onSelect = useCallback(() => {
      if (!emblaApi) return;
      const newIndex = emblaApi.selectedScrollSnap();
      setSelectedIndex(newIndex);
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());

      // Track navigation after index updates
      if (lastNavigationDirectionRef.current !== null && onNavigationClick) {
        onNavigationClick(lastNavigationDirectionRef.current, newIndex);
        lastNavigationDirectionRef.current = null;
      }
    }, [emblaApi, onNavigationClick]);

    useEffect(() => {
      if (!emblaApi) return;
      onSelect();
      emblaApi.on("select", onSelect);
      emblaApi.on("reInit", onSelect);
      return () => {
        emblaApi.off("select", onSelect);
        emblaApi.off("reInit", onSelect);
      };
    }, [emblaApi, onSelect]);

    const scrollPrev = useCallback(() => {
      if (emblaApi) {
        lastNavigationDirectionRef.current = "prev";
        emblaApi.scrollPrev();
      }
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
      if (emblaApi) {
        lastNavigationDirectionRef.current = "next";
        emblaApi.scrollNext();
      }
    }, [emblaApi]);

    const selectedItem = displayData[selectedIndex] || null;

    return (
      <section
        ref={ref}
        className={cn(
          "w-full flex items-center justify-center px-5 py-8 gap-6"
        )}
        style={{ backgroundColor }}
      >
        <div
          className={cn(
            "flex flex-col gap-5 items-center justify-center w-full",
            "sm:w-[1008px]",
            "md:w-[1158px]",
            "lg:w-[1358px]",
            "xl:w-[1538px]"
          )}
        >
          {title && (
            <h2
              className={cn(
                "text-heading-6-semibold",
                "text-center",
                "line-clamp-1"
              )}
              style={{ color: titleTextColor }}
            >
              {title}
            </h2>
          )}

          <div className="flex flex-col gap-5 items-center justify-center w-full">
            {showNavigationButtons && (
              <div className="flex items-center justify-center gap-[8px] w-full">
                {/* Left Arrow */}
                <button
                  onClick={scrollPrev}
                  disabled={!canScrollPrev}
                  className={cn(
                    "flex items-center justify-center flex-shrink-0 w-9 h-9",
                    canScrollPrev
                      ? "cursor-pointer"
                      : "cursor-not-allowed opacity-50"
                  )}
                  aria-label="Previous slide"
                >
                  {leftNavIcon ? (
                    <Image
                      src={leftNavIcon}
                      alt={leftNavIconAlt}
                      width={36}
                      height={36}
                      className="object-contain"
                    />
                  ) : (
                    <NavigationArrow
                      className="xs:size-9 rotate-180"
                      fill={canScrollPrev ? "#F74958" : "#F8B6BC"}
                      pathFill="#F8F9FA"
                    />
                  )}
                </button>

                {/* Carousel - Single Card View */}
                <div
                  className="rounded-[18px] shadow-[0px_4px_14.8px_0px_rgba(0,0,0,0.15)] overflow-hidden"
                  ref={emblaRef}
                >
                  <div className="flex">
                    {displayData.map((item) => (
                      <div
                        key={item.id}
                        className="flex-[0_0_100%] min-w-0 flex gap-2 items-center px-[10px] py-6"
                        style={{
                          backgroundColor:
                            item?.mobile_v1_title_card_color || "#F74958",
                        }}
                      >
                        {/* Icon */}
                        {item?.mobile_v1_icon && (
                          <div className="w-[34px] h-[34px] flex-shrink-0 relative">
                            <Image
                              src={item?.mobile_v1_icon || ""}
                              alt={item.mobile_v1_icon_alt || item.card_title}
                              fill
                              className="object-contain"
                            />
                          </div>
                        )}

                        {/* Card Text */}
                        {item?.card_title && (
                          <p
                            className="text-body-1-regular w-[155px] line-clamp-3"
                            style={{
                              color:
                                item?.mobile_v1_title_text_color || "#F8F9FA",
                            }}
                          >
                            {item?.card_title}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Arrow */}
                <button
                  onClick={scrollNext}
                  disabled={!canScrollNext}
                  className={cn(
                    "flex items-center justify-center flex-shrink-0 w-9 h-9",
                    canScrollNext
                      ? "cursor-pointer"
                      : "cursor-not-allowed opacity-50"
                  )}
                  aria-label="Next slide"
                >
                  {rightNavIcon ? (
                    <Image
                      src={rightNavIcon || ""}
                      alt={rightNavIconAlt}
                      width={36}
                      height={36}
                      className="object-contain"
                    />
                  ) : (
                    <NavigationArrow
                      className="xs:size-9"
                      fill={canScrollNext ? "#F74958" : "#F8B6BC"}
                      pathFill="#F8F9FA"
                    />
                  )}
                </button>
              </div>
            )}

            {selectedItem && selectedItem?.detail_description && (
              <div
                className={cn(
                  "border border-[#F8B6BC] border-solid w-full",
                  "flex flex-col items-center justify-center p-4",
                  "rounded-[18px] shadow-[0px_4px_14.8px_0px_rgba(0,0,0,0.15)]"
                )}
                style={{
                  backgroundColor:
                    selectedItem?.mobile_v1_description_card_color || "#FFFFFF",
                }}
              >
                <p
                  className="text-body-3-regular text-center line-clamp-10"
                  style={{
                    color:
                      selectedItem?.mobile_v1_description_text_color ||
                      "#F74958",
                  }}
                >
                  {selectedItem?.detail_description ||
                    selectedItem?.card_description}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }
);

export default CourseSpecialisationsCarousel;
